import { FormattedMessage } from 'react-intl';
import { Spinner } from '@anyroad/spinner';
import { TimeSlotSelector } from '@anyroad/time-slot-selector';
import type { TimeSlot } from '@anyroad/time-slot-selector';
import { ExperiencePricingRules } from 'views/plugins/tours/page/booking_details/pricing-rules/ExperiencePricingRules';
import type { GuestsByTicketType, PricingRule } from '../types';

import {
  Label,
  SpinnerContainer,
  TimeSlotsErrorLabel,
  ExperiencePricingRulesContainer,
} from '../styles';

type TimeSlotSelectionProps = {
  availableTimes: TimeSlot[];
  selectedTimeId: string | undefined;
  onTimeSlotChange: (id: string | undefined) => void;
  numberOfGuests: number;
  guestsByTicketType?: GuestsByTicketType;
  isCapacityByTicketType: boolean;
  isoTime: boolean;
  pricingRulesMap: Map<string, PricingRule> | null;
  isLoadingTimeSlots: boolean;
  isTimeSlotsError: boolean;
  hasRescheduleMutationError: boolean;
  selectedDate: Date | null;
  noTimesOnBookingDate: boolean;
};

export const TimeSlotSelection = ({
  availableTimes,
  selectedTimeId,
  onTimeSlotChange,
  numberOfGuests,
  guestsByTicketType,
  isCapacityByTicketType,
  isoTime,
  pricingRulesMap,
  isLoadingTimeSlots,
  isTimeSlotsError,
  hasRescheduleMutationError,
  selectedDate,
  noTimesOnBookingDate,
}: TimeSlotSelectionProps) => {
  if (hasRescheduleMutationError) {
    return (
      <TimeSlotsErrorLabel>
        <FormattedMessage defaultMessage="An error occurred. Please try again." />
      </TimeSlotsErrorLabel>
    );
  }

  if (!selectedDate) {
    return (
      <Label>
        <FormattedMessage defaultMessage="Please select a date first" />
      </Label>
    );
  }

  if (isLoadingTimeSlots) {
    return (
      <SpinnerContainer>
        <Spinner />
      </SpinnerContainer>
    );
  }

  if (isTimeSlotsError) {
    return (
      <TimeSlotsErrorLabel>
        <FormattedMessage defaultMessage="Unable to load available times. Please try another date." />
      </TimeSlotsErrorLabel>
    );
  }

  if (availableTimes.length === 0) {
    return (
      <Label>
        <FormattedMessage defaultMessage="No available times for this date." />
      </Label>
    );
  }

  if (noTimesOnBookingDate) {
    return (
      <Label>
        <FormattedMessage defaultMessage="No other times available on this date." />
      </Label>
    );
  }

  const timeSlotProps = {
    availableTimes,
    selectedTimeId,
    onChange: (id: string | null | undefined) =>
      onTimeSlotChange(id ?? undefined),
    guests: numberOfGuests,
    isCapacityByTicketType,
    isoTime,
    showAvailableLabel: true as const,
    showPlacesLeft: false as const,
    ...(isCapacityByTicketType &&
    guestsByTicketType != null &&
    Object.keys(guestsByTicketType).length > 0
      ? { guestsByTicketType }
      : {}),
  };

  return (
    <>
      <TimeSlotSelector {...timeSlotProps} />
      <ExperiencePricingRulesContainer>
        <ExperiencePricingRules pricingRules={pricingRulesMap} />
      </ExperiencePricingRulesContainer>
    </>
  );
};
